import { faker } from '@faker-js/faker';
import { factory, manyOf, nullable, oneOf, primaryKey } from '@mswjs/data';
import { v4 as uuid } from 'uuid';

import { BASE_64_IMAGES, STREAM_URLS, WAYPOINTS } from './constants';
import { ROUTE_A, ROUTE_B, ROUTE_C } from './routes';
import { fakePosition } from './utils';

import {
    AdsState,
    ConnectivityState,
    DeviationState,
    DriverMode,
    DrivingState,
    Error,
    MissionEventType,
    MissionStateValue,
    MRMStateValue,
    MrmExecutionPhase,
    MrmTriggerValue,
    MrmType,
    Reason,
    Severity,
    Status,
    VideoStreamingErrorCode,
} from 'API';

faker.seed(1337);

export const VINS = [
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
    faker.vehicle.vin(),
];
export const MISSION_IDS = [
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
    faker.string.uuid(),
];
export const HUB_IDS = [faker.string.uuid(), faker.string.uuid(), faker.string.uuid(), faker.string.uuid()];

const position = {
    latitude: () => Number(fakePosition().latitude),
    longitude: () => Number(fakePosition().longitude),
    heading: () => Number(fakePosition().heading),
    altitude: () => Number(fakePosition().altitude),
};

const connectivityStatus = {
    state: () => faker.helpers.arrayElement(Object.values(ConnectivityState)),
    expectedDisconnect: () => faker.datatype.boolean(),
    latency: nullable(() => faker.number.int({ min: 0, max: 1000 })),
    reason: () => faker.helpers.arrayElement(Object.values(Reason)),
    description: () => faker.lorem.text(),
};

const state = {
    updatedAt: () => faker.date.anytime().toISOString(),
    value: () => faker.helpers.arrayElement(Object.values(MissionStateValue)),
};

const mrmState = {
    updatedAt: () => faker.date.anytime().toISOString(),
    value: () => faker.helpers.arrayElement(Object.values(MRMStateValue)),
    triggerBy: () => faker.helpers.arrayElement(Object.values(MrmTriggerValue)),
    mrmExecutionPhase: () => faker.helpers.arrayElement(Object.values(MrmExecutionPhase)),
    mrmType: () => faker.helpers.arrayElement(Object.values(MrmType)),
    causes: nullable(() => faker.helpers.arrayElements([]) as any),
    error: nullable(() => faker.helpers.arrayElement(Object.values(Error))),
};

// Data Model
export const MockDatabase = factory({
    role: {
        id: primaryKey(faker.string.uuid),
        name: () => faker.company.name(),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
        claims: manyOf('claim'),
        useWildcardClaims: () => faker.datatype.boolean(),
    },
    claim: {
        id: primaryKey(() => faker.string.uuid()),
        pattern: () => faker.lorem.words(1),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
    },
    hub: {
        id: primaryKey(() => faker.helpers.arrayElement(HUB_IDS)),
        name: () => faker.company.name(),
        address: () => faker.location.streetAddress(),
        position,
        active: () => faker.datatype.boolean(),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
    },
    user: {
        id: primaryKey(faker.string.uuid),
        firstName: () => `${faker.person.firstName()}`,
        lastName: () => `${faker.person.lastName()}`,
        phone: () => faker.lorem.words(1),
        email: () => faker.lorem.words(1),
        country: () => faker.lorem.words(1),
        city: () => faker.lorem.words(1),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
        status: () => faker.helpers.arrayElement(Object.values(Status)),
        teams: manyOf('team'),
        roles: manyOf('role'),
    },
    systemTeam: {
        id: primaryKey(faker.string.uuid),
        name: () => faker.lorem.words(1),
        numberOfFleets: () => faker.number.int(),
        numberOfUsers: () => faker.number.int(),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
    },
    teamUserInfo: {
        id: primaryKey(faker.string.uuid),
        firstName: () => faker.person.firstName(),
        lastName: () => faker.person.lastName(),
    },
    teamFleetInfo: {
        id: primaryKey(faker.string.uuid),
        name: () => faker.company.name(),
    },
    team: {
        id: primaryKey(faker.string.uuid),
        name: () => faker.lorem.words(1),
    },
    systemFleet: {
        id: primaryKey(faker.string.uuid),
        name: () => faker.company.name(),
        numberOfVehicles: () => faker.number.int(),
        teams: manyOf('team'),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
    },
    vehicle: {
        vin: primaryKey(faker.vehicle.vin),
        externalId: () => faker.string.uuid(),
        customerId: () => faker.string.uuid(),
        name: () => faker.person.firstName(),
        licensePlate: () => faker.vehicle.vrm(),
        position,
        adsState: () => faker.helpers.arrayElement(Object.values(AdsState)),
        drivingState: () => faker.helpers.arrayElement(Object.values(DrivingState)),
        driverMode: () => faker.helpers.arrayElement(Object.values(DriverMode)),
        speedInMs: () => faker.number.float(),
        weightInKg: () => faker.number.float(),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
        latestVehicleUpdate: () => faker.date.recent().toISOString(),
        connectivityStatus,
        mrmState,
        deviations: manyOf('deviation'),
        owners: manyOf('owner'),
        activeMissionId: () => faker.helpers.arrayElement(MISSION_IDS),
        cameras: manyOf('camera'),
    },
    deviation: {
        id: primaryKey(faker.string.uuid),
        severity: () => faker.helpers.arrayElement(Object.values(Severity)),
        category: () => faker.string.sample(),
        description: () => faker.lorem.text(),
        origin: () => faker.company.buzzNoun(),
        sentAt: () => faker.date.anytime().toISOString(),
        isBlocking: () => faker.datatype.boolean(),
        relatedDeviations: () => faker.helpers.arrayElements([]) as any,
        vin: () => faker.vehicle.vin(),
        resolvedAt: nullable(() => faker.date.anytime().toISOString()),
        state: () => faker.helpers.arrayElement(Object.values(DeviationState)),
    },
    owner: {
        id: primaryKey(faker.string.uuid),
        fullName: () => `${faker.person.firstName()} ${faker.person.lastName()}`,
        position: () => faker.person.jobTitle(),
        phoneNumber: () => faker.phone.number(),
        email: () => faker.internet.email(),
    },
    missionEvent: {
        id: primaryKey(faker.string.uuid),
        type: () => faker.helpers.arrayElement(Object.values(MissionEventType)),
        createdAt: () => faker.date.anytime().toISOString(),
        position,
    },
    mission: {
        id: primaryKey(() => faker.helpers.arrayElement(MISSION_IDS)),
        actualArrivalTime: () => faker.date.anytime().toISOString(),
        actualStartTime: () => faker.date.anytime().toISOString(),
        createdAt: () => faker.date.anytime().toISOString(),
        description: () => faker.lorem.paragraph(),
        name: () => faker.person.firstName(),
        plannedArrivalTime: () => faker.date.anytime().toISOString(),
        progressDetails: oneOf('progressDetails'),
        route: oneOf('generatedRoute'),
        state,
        updatedAt: () => faker.date.anytime().toISOString(),
        vin: () => faker.helpers.arrayElement(VINS),
        events: manyOf('missionEvent'),
    },
    progressDetails: {
        id: primaryKey(faker.string.uuid),
        eta: () => faker.date.anytime().toISOString(),
        remainingDistanceInMeters: () => faker.number.int(), // TODO: define a better range
        traveledDistanceInMeters: () => faker.number.int(),
        updatedAt: () => faker.date.anytime().toISOString(),
    },
    route: {
        id: primaryKey(() => faker.string.uuid()),
        createdAt: () => faker.date.anytime().toISOString(),
        averageDuration: () => faker.number.int({ min: 60, max: 4 * 3600 }),
        landingHub: oneOf('hub'),
        launchHub: oneOf('hub'),
        distance: () => faker.number.int({ min: 1200, max: 500_000 }),
        updatedAt: () => faker.date.anytime().toISOString(),
        // TODO: find a cleaner way to define this.
        // Actually we just need the fixed values from our waypoints mock but still need some type here
        waypoints: () => faker.helpers.arrayElements([]) as any,
    },
    generatedRoute: {
        id: primaryKey(() => faker.string.uuid()),
        createdAt: () => faker.date.anytime().toISOString(),
        durationInSeconds: () => faker.number.int({ min: 60, max: 4 * 3600 }),
        externalRouteId: () => faker.string.uuid(),
        landingHub: oneOf('hub'),
        landingHubId: () => faker.helpers.arrayElement(HUB_IDS),
        launchHub: oneOf('hub'),
        launchHubId: () => faker.helpers.arrayElement(HUB_IDS),
        predefinedRouteId: () => faker.string.uuid(),
        totalDistanceInMeters: () => faker.number.int({ min: 1200, max: 500_000 }),
        updatedAt: () => faker.date.anytime().toISOString(),
        // TODO: find a cleaner way to define this.
        // Actually we just need the fixed values from our waypoints mock but still need some type here
        waypoints: () => faker.helpers.arrayElements([]) as any,
    },
    hereAddress: {
        label: primaryKey(() => faker.location.streetAddress()),
        countryCode: () => faker.location.countryCode(),
        countryName: () => faker.location.country(),
        state: () => faker.location.state(),
        county: () => faker.location.county(),
        city: () => faker.location.city(),
        district: () => faker.location.zipCode(),
        street: () => faker.location.street(),
        postalCode: () => faker.location.zipCode(),
        houseNumber: () => faker.location.buildingNumber(),
    },
    hereAddressItem: {
        id: primaryKey(() => faker.string.uuid()),
        title: () => faker.location.street(),
        houseNumberType: () => faker.location.buildingNumber(),
        distance: () => faker.number.float(),
        address: oneOf('hereAddress'),
        position: {
            lat: () => faker.location.latitude(),
            lng: () => faker.location.longitude(),
        },
    },
    cameraImage: {
        value: () => faker.helpers.arrayElement(BASE_64_IMAGES),
        timestamp: primaryKey(() => faker.date.anytime().toISOString()),
    },
    videoStream: {
        vin: () => faker.helpers.arrayElement(VINS),
        cameraID: primaryKey(() => faker.string.uuid()),
        streamingURL: () => faker.helpers.arrayElement(STREAM_URLS),
    },
    videoStreamingError: {
        id: primaryKey(() => faker.string.uuid()),
        createdAt: () => faker.date.anytime().toISOString(),
        updatedAt: () => faker.date.anytime().toISOString(),
        vin: () => faker.helpers.arrayElement(VINS),
        cameraID: nullable(String),
        errorMessage: () => faker.lorem.sentence(),
        errorCode: () => faker.helpers.arrayElement(Object.values(VideoStreamingErrorCode)),
    },
    camera: {
        name: primaryKey(() => faker.word.noun()),
        id: nullable<string>(() => null),
    },
});

MockDatabase.team.create({
    id: uuid(),
    name: 'CC',
});

MockDatabase.team.create({
    id: uuid(),
    name: 'Scania',
});

MockDatabase.team.create({
    id: uuid(),
    name: 'MAN',
});

MockDatabase.claim.create({
    id: uuid(),
    pattern: 'asdsad',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
});

MockDatabase.role.create({
    id: uuid(),
    name: 'Technical Supervisor',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    claims: MockDatabase.claim.getAll(),
    useWildcardClaims: true,
});

MockDatabase.role.create({
    id: uuid(),
    name: 'System Admin',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    claims: MockDatabase.claim.getAll(),
    useWildcardClaims: true,
});

MockDatabase.role.create({
    id: uuid(),
    name: 'User',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    claims: MockDatabase.claim.getAll(),
    useWildcardClaims: true,
});

// Users
MockDatabase.user.create({
    id: uuid(),
    firstName: 'Josh',
    lastName: 'Doe',
    email: 'jdoe@asd.com',
    phone: '123234',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    city: 'London',
    country: 'Portugal',
    status: Status.ACTIVE,
    teams: MockDatabase.team.getAll(),
    roles: MockDatabase.role.getAll(),
});
MockDatabase.user.create({
    id: uuid(),
    firstName: 'Ash',
    lastName: 'Ketchum',
    email: 'ash.ketchum@pokemon.com',
    phone: '22',
    createdAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    city: 'Pallet Town',
    country: 'Kanto',
    status: Status.ACTIVE,
    teams: MockDatabase.team.getAll(),
    roles: MockDatabase.role.getAll(),
});

// Teams
MockDatabase.team.create({
    name: 'European Squad',
});

MockDatabase.team.create({
    name: 'American Crew',
});

MockDatabase.team.create({
    name: 'Asian Squad',
});

// Fleets
MockDatabase.systemFleet.create({
    name: 'MAN Test Fleet',
    numberOfVehicles: 20,
    teams: MockDatabase.team.getAll(),
    createdAt: '2025-11-16T10:00:20.299Z',
});
MockDatabase.systemFleet.create({
    name: 'SCANIA Test Fleet',
    numberOfVehicles: 10,
    teams: MockDatabase.team.getAll(),
    createdAt: '2025-11-16T10:00:20.299Z',
});
MockDatabase.systemFleet.create({
    name: 'NAVISTAR Test Fleet',
    numberOfVehicles: 31,
    teams: MockDatabase.team.getAll(),
    createdAt: '2025-11-16T10:00:20.299Z',
});

// System Teams
MockDatabase.systemTeam.create({
    name: 'Test Teams A',
    numberOfFleets: 2,
    numberOfUsers: 10,
    createdAt: '2025-11-16T10:00:20.299Z',
    updatedAt: '2025-11-16T10:00:20.299Z',
});
MockDatabase.systemTeam.create({
    name: 'Test Teams B',
    numberOfFleets: 3,
    numberOfUsers: 5,
    createdAt: '2026-05-07T10:00:20.299Z',
    updatedAt: '2026-05-07T10:00:20.299Z',
});

MockDatabase.systemTeam.create({
    name: 'Test Teams C',
    numberOfFleets: 1,
    numberOfUsers: 15,
    createdAt: '2026-05-07T12:00:20.299Z',
    updatedAt: '2026-05-07T12:00:20.299Z',
});

// Hubs
MockDatabase.hub.create({
    id: HUB_IDS[0],
    name: 'Hub I',
    active: true,
    address: faker.location.streetAddress(),
    position: {
        latitude: 48.3418733,
        longitude: 11.6067098,
    },
});

MockDatabase.hub.create({
    id: HUB_IDS[1],
    name: 'Hub II',
    address: faker.location.streetAddress(),
    active: true,
    position: {
        latitude: 48.3866331,
        longitude: 11.5974676,
    },
});

MockDatabase.hub.create({
    id: HUB_IDS[2],
    name: 'Hub Riem Arcaden',
    address: faker.location.streetAddress(),
    active: true,
    position: {
        latitude: 48.13243255052443,
        longitude: 11.691210499999999,
    },
});

MockDatabase.hub.create({
    id: HUB_IDS[3],
    name: 'Hub Augsburg Airport',
    address: faker.location.streetAddress(),
    active: true,
    position: {
        latitude: 48.42788804304301,
        longitude: 10.952264305199673,
    },
});

MockDatabase.owner.create({
    id: uuid(),
    fullName: 'Autobots',
    email: 'autobots@email.com',
    position: 'Transformer',
    phoneNumber: '999',
});

MockDatabase.owner.create({
    id: uuid(),
    fullName: 'Decepticons',
    email: 'decepticons@email.com',
    position: 'Transformer',
    phoneNumber: '999',
});

const launchHubA = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Launch Hub A',
    position: {
        latitude: 48.154193,
        longitude: 11.616034,
    },
});

const landingHubA = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Landing Hub A',
    position: {
        latitude: 48.24907900000009,
        longitude: 11.653157999999989,
    },
});

const missionRouteA = MockDatabase.generatedRoute.create({
    waypoints: ROUTE_A,
    launchHub: launchHubA,
    landingHub: landingHubA,
});

const launchHubB = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Launch Hub B',
    position: {
        latitude: 48.213,
        longitude: 11.46752,
    },
});

const landingHubB = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Landing Hub B',
    position: {
        latitude: 48.15251599999995,
        longitude: 11.506938000000005,
    },
});

const missionRouteB = MockDatabase.generatedRoute.create({
    waypoints: ROUTE_B,
    launchHub: launchHubB,
    landingHub: landingHubB,
});

const launchHubC = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Launch Hub C',
    position: {
        latitude: 48.136275,
        longitude: 11.579424,
    },
});

const landingHubC = MockDatabase.hub.create({
    id: faker.string.uuid(),
    name: 'Landing Hub C',
    position: {
        latitude: 48.35666999999999,
        longitude: 10.984190000000012,
    },
});

const missionRouteC = MockDatabase.generatedRoute.create({
    waypoints: ROUTE_C,
    launchHub: launchHubC,
    landingHub: landingHubC,
});

/**
 * Bumblebee
 * - connectivityStatus: ONLINE
 * - In MRC
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Bumblebee',
    vin: VINS[0],
    drivingState: DrivingState.DRIVING,
    position: {
        latitude: 48.209909999999994,
        longitude: 11.467569999999998,
        heading: 0.0,
    },
    mrmState: {
        value: MRMStateValue.MRC,
        updatedAt: new Date().toISOString(),
    },
    activeMissionId: MISSION_IDS[0],
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
        expectedDisconnect: false,
        latency: 500,
    },
    owners: [MockDatabase.owner.create({ fullName: 'Autobots' }), MockDatabase.owner.create()],
    deviations: [
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
    ],
    cameras: [
        MockDatabase.camera.create({ id: faker.string.uuid() }),
        MockDatabase.camera.create({ id: faker.string.uuid() }),
        MockDatabase.camera.create({ id: faker.string.uuid() }),
    ],
});

/**
 * Optimus Prime
 * - connectivityStatus: OFFLINE, expectedDisconnect: false
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Optimus Prime',
    vin: VINS[1],
    drivingState: DrivingState.UNKNOWN,
    mrmState: {
        value: MRMStateValue.NONE,
    },
    activeMissionId: MISSION_IDS[1],
    position: {
        latitude: 48.183710000000026,
        longitude: 11.627390000000007,
        heading: 0,
        altitude: 0,
    },
    connectivityStatus: {
        state: ConnectivityState.OFFLINE,
        expectedDisconnect: false,
    },
    speedInMs: 0,
    deviations: [
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
    ],
    owners: [MockDatabase.owner.create()],
    cameras: [MockDatabase.camera.create({ id: faker.string.uuid() })],
});

/**
 * Megatron
 * - connectivityStatus: ONLINE
 * - In MRC
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Megatron',
    vin: VINS[2],
    drivingState: DrivingState.PARKED,
    activeMissionId: MISSION_IDS[2],
    position: {
        latitude: 48.353149999999985,
        longitude: 11.133449999999995,
        heading: 0.0,
    },
    mrmState: {
        value: MRMStateValue.NONE,
        updatedAt: new Date().toISOString(),
    },
    connectivityStatus: {
        state: ConnectivityState.OFFLINE,
        expectedDisconnect: true,
        latency: null,
    },
    owners: [MockDatabase.owner.create(), MockDatabase.owner.create()],
    cameras: [MockDatabase.camera.create({ id: undefined, name: 'DEFAULT' })],
});

/**
 * Starscream
 * - connectivityStatus: OFFLINE, expectedDisconnect: true
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Starscream',
    vin: VINS[5],
    drivingState: DrivingState.PARKED,
    activeMissionId: undefined,
    position: {
        latitude: 48.423773765612154,
        longitude: 10.9199119181742,
        heading: 0.0,
    },
    mrmState: {
        value: MRMStateValue.MRC,
        updatedAt: new Date().toISOString(),
    },
    connectivityStatus: {
        state: ConnectivityState.OFFLINE,
        expectedDisconnect: true,
    },
    deviations: [],
    owners: [],
    cameras: [
        MockDatabase.camera.create({ id: faker.string.uuid() }),

        MockDatabase.camera.create({ id: faker.string.uuid() }),

        MockDatabase.camera.create({ id: faker.string.uuid() }),
    ],
});

/**
 * Blackout
 * - connectivityStatus: ONLINE
 */
const devId = uuid();
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Blackout',
    vin: VINS[6],
    activeMissionId: MISSION_IDS[6],
    mrmState: {
        value: MRMStateValue.EXECUTING_MRM,
        mrmExecutionPhase: MrmExecutionPhase.MRM_EXECUTION_PHASE_PRE_DECELERATION,
        causes: [{ deviationId: String(devId) }],
    },
    position: {
        latitude: 48.41959268770702,
        longitude: 10.919932745248357,
        heading: 0.0,
    },
    driverMode: DriverMode.DRIVER_MODE_IN_SUPERVISED,
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
    },
    deviations: [
        MockDatabase.deviation.create({
            id: devId,
            description: 'Fatal',
            category: 'Fatal occurence 1',
            origin: 'connectivity > vehicle > wifi > whatever > fhjdshfjdshf',
            severity: Severity.FATAL,
            isBlocking: false,
            resolvedAt: null,
        }),
        MockDatabase.deviation.create({
            description: 'Issue description',
            category: 'Info occurence 2',
            severity: Severity.INFO,
            isBlocking: false,
            resolvedAt: new Date('2024-08-20T13:30:00Z').toISOString(),
        }),
    ],
    owners: [],
    cameras: [
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'a' }),

        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'b' }),

        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'c' }),
    ],
});

/*
	Workaround to prevent msw/data to create activeMissionIds although setting them explicitly as undefined
	const [_, ...vehiclesWithoutMission] = VINS
*/

// no mission assigned
MockDatabase.vehicle.create({
    vin: VINS[3],
    name: 'Truck w/o mission',
    drivingState: DrivingState.DRIVING,
    cameras: [
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'e' }),
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'f' }),
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'g' }),
    ],
    deviations: [
        MockDatabase.deviation.create({
            description: 'Warning',
            category: 'Fatal occurence 3',
            severity: Severity.WARNING,
            isBlocking: false,
        }),
    ],
});

/**
 * Routes
 */

const ALLHUBS = MockDatabase.hub.getAll();

MockDatabase.route.create({
    launchHub: ALLHUBS[0],
    landingHub: ALLHUBS[1],
    averageDuration: 6 * 3600, // 6 hours
    waypoints: WAYPOINTS.map(({ latitude, longitude }) => ({
        latitude,
        longitude,
    })),
    distance: 500_000,
});

MockDatabase.route.create({
    launchHub: ALLHUBS[2],
    landingHub: ALLHUBS[3],
    averageDuration: 50 * 60,
    waypoints: WAYPOINTS.map(({ latitude, longitude }) => ({
        latitude,
        longitude,
    })),
    distance: 82_000,
});

const ALLROUTES = MockDatabase.route.getAll();

MockDatabase.generatedRoute.create({
    id: ALLROUTES[0]?.id,
    launchHub: ALLHUBS[0],
    landingHub: ALLHUBS[1],
    launchHubId: ALLHUBS[0]?.id,
    landingHubId: ALLHUBS[1]?.id,
    durationInSeconds: 6 * 3600, // 6 hours
    waypoints: WAYPOINTS.map(({ latitude, longitude }) => ({
        latitude,
        longitude,
    })),
    totalDistanceInMeters: 500_000,
});

MockDatabase.generatedRoute.create({
    id: ALLROUTES[1]?.id,
    launchHub: ALLHUBS[2],
    landingHub: ALLHUBS[3],
    durationInSeconds: 50 * 60,
    launchHubId: ALLHUBS[2]?.id,
    landingHubId: ALLHUBS[3]?.id,
    waypoints: WAYPOINTS.map(({ latitude, longitude }) => ({
        latitude,
        longitude,
    })),
    totalDistanceInMeters: 82_000,
});

const ALLGENERATEDROUTES = MockDatabase.generatedRoute.getAll();

// assigned
MockDatabase.mission.create({
    id: MISSION_IDS[3],
    vin: VINS[4],
    name: `${ALLROUTES[0]?.launchHub?.name} <> ${ALLROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.READY_TO_LAUNCH,
    },
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    route: ALLGENERATEDROUTES[0],
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:07:00Z').toISOString(),
        traveledDistanceInMeters: 480_000,
        remainingDistanceInMeters: 20_000,
    }),
});

// in proggress - on time
MockDatabase.mission.create({
    id: MISSION_IDS[0],
    vin: VINS[0],
    name: `${ALLROUTES[0]?.launchHub?.name} <> ${ALLROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.READY_TO_LAUNCH,
    },
    route: missionRouteB,
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:30:00Z').toISOString(),
        traveledDistanceInMeters: 10_000,
        remainingDistanceInMeters: 490_000,
    }),
    events: [
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T12:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_STARTED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T13:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_PAUSED_BY_MRM,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T14:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_RESUMED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_PAUSED_BY_MRM,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_RESUMED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_COMPLETED,
        }),
    ],
});

// in progress - early
MockDatabase.mission.create({
    id: MISSION_IDS[1],
    vin: VINS[1],
    name: `${ALLGENERATEDROUTES[0]?.launchHub?.name} <> ${ALLGENERATEDROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.EXECUTING,
    },
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    route: missionRouteA,
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:07:00Z').toISOString(),
        traveledDistanceInMeters: 480_000,
        remainingDistanceInMeters: 20_000,
    }),
    events: [
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T12:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_STARTED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T13:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_PAUSED_BY_MRM,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T14:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_RESUMED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_PAUSED_BY_MRM,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_RESUMED,
        }),
        MockDatabase.missionEvent.create({
            createdAt: new Date('2024-08-22T15:00:00.000Z').toISOString(),
            type: MissionEventType.MISSION_COMPLETED,
        }),
    ],
});

// in progress - delayed
MockDatabase.mission.create({
    id: MISSION_IDS[2],
    vin: VINS[2],
    name: `${ALLGENERATEDROUTES[0]?.launchHub?.name} <> ${ALLGENERATEDROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.ACTIVE,
    },
    route: missionRouteC,
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:30:00Z').toISOString(),
        traveledDistanceInMeters: 0,
        remainingDistanceInMeters: 500_000,
    }),
});

// complete - delayed
MockDatabase.mission.create({
    id: MISSION_IDS[6],
    vin: VINS[6],
    name: `${ALLGENERATEDROUTES[1]?.launchHub?.name} <> ${ALLGENERATEDROUTES[1]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.COMPLETE,
    },
    actualStartTime: new Date('2024-08-20T13:00:00Z').toISOString(),
    plannedArrivalTime: new Date('2024-08-20T13:50:00Z').toISOString(),
    actualArrivalTime: new Date('2024-08-20T14:15:00Z').toISOString(),
    route: ALLGENERATEDROUTES[1],
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T14:15:00Z').toISOString(),
        traveledDistanceInMeters: 82_000,
        remainingDistanceInMeters: 0,
    }),
});

MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Ironhide',
    vin: VINS[8],
    drivingState: DrivingState.PARKED,
    activeMissionId: MISSION_IDS[8],
    position: {
        latitude: 48.27,
        longitude: 11.55,
        heading: 0.0,
    },
    mrmState: {
        value: MRMStateValue.MRC,
        updatedAt: new Date().toISOString(),
    },
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
        expectedDisconnect: false,
        latency: 100,
    },
    owners: [MockDatabase.owner.create()],
    deviations: [
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: true,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
    ],
    cameras: [],
});

// Deviation simulation vehicle — full contract coverage is injected by the
// GetVehicleByVINV2 handler (see mocks/graphql.ts + mocks/data/deviationScenarios.ts).
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Deviation Sim',
    vin: VINS[7],
    drivingState: DrivingState.PARKED,
    activeMissionId: undefined,
    position: {
        latitude: 48.3,
        longitude: 11.6,
        heading: 0.0,
    },
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
        expectedDisconnect: false,
        latency: 50,
    },
    owners: [MockDatabase.owner.create({ fullName: 'Deviation QA' })],
    deviations: [],
    cameras: [],
});

// MRC + READY_TO_RESUME — for testing: cancel mission and create new mission while vehicle is in MRC
MockDatabase.mission.create({
    id: MISSION_IDS[8],
    vin: VINS[8],
    name: `${ALLGENERATEDROUTES[0]?.launchHub?.name} <> ${ALLGENERATEDROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.READY_TO_RESUME,
    },
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    plannedArrivalTime: new Date('2024-08-20T15:00:00Z').toISOString(),
    route: ALLGENERATEDROUTES[0],
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T15:00:00Z').toISOString(),
        traveledDistanceInMeters: 200_000,
        remainingDistanceInMeters: 300_000,
    }),
});

/**
 * Sentinel
 * - connectivityStatus: ONLINE
 * - Active mission: EXECUTING, in progress
 * - progressDetails.updatedAt set far in the past to emulate outdated/stale data
 *   when used with VITE_FORCE_OUTDATED=true or a V2 TTL-aware field
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Sentinel',
    vin: VINS[9],
    drivingState: DrivingState.DRIVING,
    adsState: AdsState.AUTONOMOUS,
    driverMode: DriverMode.DRIVER_MODE_OUT_AUTOMATED,
    speedInMs: 22.5,
    activeMissionId: MISSION_IDS[9],
    position: {
        latitude: 48.19,
        longitude: 11.54,
        heading: 90.0,
    },
    mrmState: {
        value: MRMStateValue.NONE,
        updatedAt: new Date().toISOString(),
    },
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
        expectedDisconnect: false,
        latency: 42,
    },
    owners: [MockDatabase.owner.create({ fullName: 'Autobots' })],
    deviations: [
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
        MockDatabase.deviation.create({
            description: 'Connection failed',
            origin: 'cc.connectivity',
            severity: Severity.ERROR,
            isBlocking: false,
        }),
    ],
    cameras: [
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'front' }),
        MockDatabase.camera.create({ id: faker.string.uuid(), name: 'rear' }),
    ],
});

MockDatabase.mission.create({
    id: MISSION_IDS[9],
    vin: VINS[9],
    name: `${ALLGENERATEDROUTES[0]?.launchHub?.name} <> ${ALLGENERATEDROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.EXECUTING,
    },
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    route: missionRouteA,
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:20:00Z').toISOString(),
        traveledDistanceInMeters: 250_000,
        remainingDistanceInMeters: 250_000,
        updatedAt: new Date(0).toISOString(),
    }),
});

/**
 * Galvatron
 * - connectivityStatus: ONLINE
 * - Active mission: EXECUTING, in progress
 * - V2 fields carry errors (ErrorTypeV2.UNKNOWN / UNSPECIFIED) instead of stale values
 *   making the error-state UI visible
 */
MockDatabase.vehicle.create({
    externalId: uuid(),
    name: 'Galvatron',
    vin: VINS[10],
    drivingState: DrivingState.DRIVING,
    adsState: AdsState.AUTONOMOUS,
    driverMode: DriverMode.DRIVER_MODE_OUT_AUTOMATED,
    speedInMs: 19.4,
    activeMissionId: MISSION_IDS[10],
    position: {
        latitude: 48.22,
        longitude: 11.57,
        heading: 270.0,
    },
    mrmState: {
        value: MRMStateValue.NONE,
        updatedAt: new Date().toISOString(),
    },
    connectivityStatus: {
        state: ConnectivityState.ONLINE,
        expectedDisconnect: false,
        latency: 55,
    },
    owners: [MockDatabase.owner.create({ fullName: 'Decepticons' })],
    deviations: [
        MockDatabase.deviation.create({
            description: 'Sensor malfunction detected',
            category: 'Sensor error',
            origin: 'sensor.lidar',
            severity: Severity.ERROR,
            isBlocking: true,
            resolvedAt: null,
        }),
        MockDatabase.deviation.create({
            description: 'Navigation system degraded',
            category: 'Navigation warning',
            origin: 'navigation.gps',
            severity: Severity.WARNING,
            isBlocking: false,
            resolvedAt: null,
        }),
    ],
    cameras: [
        MockDatabase.camera.create({
            id: faker.string.uuid(),
            name: 'shockwave-front',
        }),
        MockDatabase.camera.create({
            id: faker.string.uuid(),
            name: 'shockwave-rear',
        }),
    ],
});

MockDatabase.mission.create({
    id: MISSION_IDS[10],
    vin: VINS[10],
    name: `${ALLGENERATEDROUTES[0]?.launchHub?.name} <> ${ALLGENERATEDROUTES[0]?.landingHub?.name}`,
    state: {
        value: MissionStateValue.EXECUTING,
    },
    plannedArrivalTime: new Date('2024-08-20T13:30:00Z').toISOString(),
    actualStartTime: new Date('2024-08-20T09:00:00Z').toISOString(),
    actualArrivalTime: undefined,
    route: missionRouteA,
    progressDetails: MockDatabase.progressDetails.create({
        eta: new Date('2024-08-20T13:20:00Z').toISOString(),
        traveledDistanceInMeters: 150_000,
        remainingDistanceInMeters: 350_000,
        updatedAt: new Date().toISOString(),
    }),
});

MockDatabase.vehicle.updateMany({
    where: {
        vin: {
            equals: VINS[5] || VINS[2],
        },
    },
    data: {
        activeMissionId: undefined,
    },
});
